import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { Aviso, Botao, Campo, CampoDeSenha } from "../componentes/ui";
import { CascaPublica } from "../componentes/layout";
import { PassoDoSegundoFator } from "../componentes/acesso/PassoDoSegundoFator";
import type { DesafioDeMfa } from "../nucleo/api";
import {
  destinoPadrao,
  ehDesafio,
  ehInterno,
  mensagemDeEntrada,
  useConcluirEntrada,
  useEntrar,
} from "../nucleo/sessao";
import { destinoAposEntrar } from "../nucleo/navegacao";
import { emailParecePlausivel } from "../nucleo/validacao";
import { useTitulo } from "../nucleo/titulo";

export function Entrar() {
  useTitulo("Entrar");

  const navegar = useNavigate();
  const { state } = useLocation();
  const entrar = useEntrar();
  const concluir = useConcluirEntrada();
  // Senha certa e falta o segundo fator: a sessão ainda NÃO existe, e a tela muda de passo.
  const [desafio, setDesafio] = useState<DesafioDeMfa | null>(null);

  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [erroLocal, setErroLocal] = useState<string | null>(null);

  // O motivo que a rota protegida trouxe (ex.: a rede da organização recusou o endereço).
  const motivoDaRota =
    typeof state === "object" && state !== null && "motivo" in state
      ? String((state as { motivo: unknown }).motivo)
      : null;
  const mensagemDeErro =
    erroLocal ?? (entrar.isError ? mensagemDeEntrada(entrar.error) : null) ?? motivoDaRota;

  async function seguir(sessao: Awaited<ReturnType<typeof concluir.mutateAsync>>) {
    const pretendido = sessao ? destinoAposEntrar(state, ehInterno(sessao)) : null;
    navegar(pretendido ?? destinoPadrao(sessao), { replace: true });
  }

  async function aoConcluirOSegundoFator() {
    try {
      await seguir(await concluir.mutateAsync());
    } catch {
      setDesafio(null);
      setErroLocal("Não foi possível concluir a entrada. Entre de novo.");
    }
  }

  async function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    setErroLocal(null);

    if (!emailParecePlausivel(email)) {
      setErroLocal("Confira o e-mail: falta o arroba ou o domínio.");
      return;
    }
    if (senha.length === 0) {
      setErroLocal("Informe a sua senha.");
      return;
    }

    try {
      const resultado = await entrar.mutateAsync({ email: email.trim(), senha });
      if (ehDesafio(resultado)) {
        setDesafio(resultado);
        return;
      }
      await seguir(resultado);
    } catch {
      // A frase que a pessoa lê sai de `mensagemDeEntrada`, e é a mesma para e-mail que não
      // existe e para senha errada. Aqui só evitamos a rejeição não tratada.
    }
  }

  if (desafio) {
    return (
      <CascaPublica
        titulo={
          desafio.etapa === "inscrever" ? "Configure a verificação em duas etapas" : "Confirme que é você"
        }
        descricao={
          desafio.etapa === "inscrever"
            ? undefined
            : "Informe o código gerado pelo seu aplicativo autenticador para concluir a entrada."
        }
      >
        <PassoDoSegundoFator
          desafio={desafio}
          aoConcluir={() => void aoConcluirOSegundoFator()}
          aoVoltar={() => {
            setDesafio(null);
            setSenha("");
            entrar.reset();
          }}
        />
      </CascaPublica>
    );
  }

  return (
    <CascaPublica
      titulo="Entrar no portal"
      descricao="Use o e-mail que recebeu o convite da GEOvendas e a senha que você definiu."
      rodape={
        <>
          <Link to="/recuperar">Esqueci a minha senha</Link>
          <Link to="/">Voltar para a página inicial</Link>
        </>
      }
    >
      <form onSubmit={(evento) => void enviar(evento)} noValidate>
        <Campo
          rotulo="E-mail"
          type="email"
          inputMode="email"
          autoComplete="username"
          placeholder="nome@empresa.com.br"
          value={email}
          onChange={(evento) => setEmail(evento.target.value)}
          disabled={entrar.isPending}
          autoFocus
          obrigatorio
        />

        <CampoDeSenha
          rotulo="Senha"
          valor={senha}
          aoMudar={setSenha}
          autoComplete="current-password"
          desabilitado={entrar.isPending}
        />

        {mensagemDeErro ? (
          <Aviso tom="erro" className="mb-4" data-testid="erro-de-entrada">
            {mensagemDeErro}
          </Aviso>
        ) : null}

        <Botao type="submit" variante="verde" larguraTotal disabled={entrar.isPending}>
          {entrar.isPending ? "Entrando…" : "Entrar"}
        </Botao>
      </form>

      <p className="acesso__ajuda">
        Ainda não tem acesso? Ele é criado pela GEOvendas: fale com o consultor responsável
        pela implantação ou com o administrador do portal na sua empresa.
      </p>
    </CascaPublica>
  );
}
